<template>
  <div class="home">
    <!-- ── SECTIONS ─────────────────────────────────────────────── -->
    <HomeHeroSection />
    <HomeHeroStats />
    <HomeHeroProfile />
    <HomeHowItWorks />
    <HomeTestimonialsCarousel
      :items="testimonials"
      :visible-desktop="2"
      :visible-mobile="1"
      :autoplay-delay="5000" />
    <HomeShowcases />
    <HomeCtaBanner />
    <HomePopularGrid v-bind="metiersPopulaires" />
    <HomePopularGrid v-bind="ecolesPopulaires" />
    <HomePopularGrid v-bind="diplomesPopulaires" />
    <HomePopularGrid v-bind="typesEcolesPopulaires" />
    <HomePopularGrid v-bind="villesPopulaires" />
    <HomePopularGrid v-bind="conseilsPopulaires" />
    <HomeProSection />
  </div>
</template>

<script setup>
definePageMeta({
  layout: 'default',
  middleware: 'redirect-if-logged-in',
})

const { t } = useI18n()
const { absoluteUrl, siteUrl } = useSeo()

// ── SEO homepage — injecté dans <head> (prerendered au build) ─
useSeoMeta({
  title: computed(() => t('homePage.seo.title')),
  description: computed(() => t('homePage.seo.description')),
  ogTitle: computed(() => t('homePage.seo.title')),
  ogDescription: computed(() => t('homePage.seo.ogDescription')),
})

useJsonLd(
  computed(() => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'PrePeers',
    url: siteUrl.value,
    logo: absoluteUrl('/logo_prepeers.svg'),
    description: t('homePage.seo.jsonLdDescription'),
    sameAs: [],
  })),
)

// ── Data statique ─────────────────────────────────────────
// const tickerItems = [
//   'HEC Paris',
//   'Sciences Po',
//   'CentraleSupélec',
//   'ESCP',
//   'Epitech',
//   'Médecin généraliste',
//   'Data Scientist',
//   'Designer UX',
//   'Architecte',
//   'Juriste',
//   'Paris',
//   'Lyon',
//   'Bordeaux',
//   'Toulouse',
//   'Nantes',
//   'Master en marketing',
//   'BTS design graphique',
//   'BUT informatique',
//   'Licence droit',
// ]

// const steps = [
//   {
//     n: '01',
//     icon: '💬',
//     title: 'Tu parles à Lola',
//     desc: 'Lola te pose des questions sur toi : tes intérêts, ta façon de penser, tes envies. Comme une vraie conversation.',
//   },
//   {
//     n: '02',
//     icon: '🧠',
//     title: 'Lola analyse ton profil',
//     desc: "Elle identifie ton type de personnalité (MBTI), tes forces et les environnements où tu t'épanouiras.",
//   },
//   {
//     n: '03',
//     icon: '🎯',
//     title: 'Tu reçois des recommandations',
//     desc: 'Formations, écoles, métiers — filtrés et classés selon ton profil, pas selon les classements génériques.',
//   },
// ]

// const categories = [
//   { icon: '🏫', title: 'Les écoles', count: '3 500+ établissements', to: '/ecoles' },
//   { icon: '💼', title: 'Les métiers', count: '4 000+ fiches métier', to: '/metiers' },
//   { icon: '📜', title: 'Les formations', count: '25 000+ programmes', to: '/ecoles' },
//   { icon: '📍', title: 'Où étudier', count: '200+ villes', to: '/ou-etudier' },
//   { icon: '🎓', title: 'Les diplômes', count: 'Bac +2 à Bac +8', to: '/diplomes' },
//   { icon: '📝', title: 'Nos conseils', count: '100+ articles', to: '/nos-conseils' },
// ]

// ── Data témoignages ──────────────────────────────────────
const testimonials = [
  {
    name: 'Manon, 21 ans',
    role: 'Étudiante en école de communication',
    initials: 'M',
    paragraphs: [
      'Après le bac, je me sentais totalement perdue devant tant de possibilités.',
      "En discutant avec Lola de PrePeers, j'ai trouvé l'école de communication qui correspond vraiment à mon profil.",
      "Aujourd'hui, je suis en 2e année et j'ai déjà pu réaliser un stage dans une grande agence de pub !",
    ],
  },
  {
    name: 'Juliette, 19 ans',
    role: "Étudiante en école d'ingénieurs",
    initials: 'J',
    paragraphs: [
      "Je ne savais pas quoi faire après le lycée… J'étais pleine de doutes… Jusqu'à ce que Lola de PrePeers m'aide à découvrir l'ingénierie environnementale.",
      "J'ai parlé avec une étudiante, et j'ai intégré une école où je travaille sur des projets concrets pour un avenir durable.",
      'Désormais, je suis prête à avoir un impact positif sur la planète !',
    ],
  },
  {
    name: 'Thomas, 22 ans',
    role: 'Étudiant Master UX Design, Paris',
    initials: 'T',
    paragraphs: [
      "J'hésitais entre le développement web et le design depuis des mois.",
      "Lola a cerné en quelques questions que j'étais fait pour le design centré utilisateur.",
      "Je suis maintenant en Master à l'ENSAD et je ne regrette absolument rien.",
    ],
  },
  {
    name: 'Camille, 20 ans',
    role: 'Étudiante en Kinésithérapie, Lyon',
    initials: 'C',
    paragraphs: [
      "J'hésitais entre médecine et biologie, deux voies très différentes.",
      "Après le test MBTI et les recommandations de Lola, j'ai réalisé que le contact humain était essentiel pour moi.",
      "J'ai choisi la kiné — et c'est parfait pour qui je suis vraiment.",
    ],
  },
  {
    name: 'Mehdi, 23 ans',
    role: 'Étudiant en École de Commerce, Bordeaux',
    initials: 'M',
    paragraphs: [
      "Trois ans de lycée avec des conseillers d'orientation qui ne comprenaient pas vraiment mes aspirations.",
      "En 20 minutes de conversation avec Lola, elle a cerné ce qu'aucun professionnel n'avait vu.",
      "Aujourd'hui j'ai un projet clair, une école qui me correspond, et enfin de la motivation !",
    ],
  },
  {
    name: 'Sarah, 18 ans',
    role: 'Étudiante en BTS Design Graphique, Nantes',
    initials: 'S',
    paragraphs: [
      "Mes parents voulaient que je fasse une licence générale. Moi je ne voulais qu'une chose : créer.",
      "PrePeers m'a aidé à structurer mon argumentaire et trouver le BTS qui correspondait à mon projet.",
      "Je n'aurais pas osé m'y inscrire seule. Maintenant je suis dans ma passion dès la première année.",
    ],
  },
]

// ── PopularGrid sections : copy statique + pills JSON / API ──
const { homePopular } = useHomePopularData()

const metiersPopulaires = computed(() => ({
  title: 'Top 30 des métiers populaires',
  subtitle: 'Tu ne sais pas quel métier faire ?',
  desc: "PrePeers analyse ton profil et les choix de milliers d'étudiants pour repérer les métiers les plus plébiscités. Inspire-toi des salaires et tendances pour affiner ton orientation et choisir une carrière qui te motive vraiment.",
  basePath: '/metiers/',
  items: [...homePopular.value.popularJobs],
}))

const ecolesPopulaires = computed(() => ({
  title: 'Les écoles populaires',
  subtitle: 'Tu veux intégrer une école reconnue ?',
  desc: "PrePeers repère pour toi les écoles les plus adaptées à ton profil. Découvre celles qui attirent le plus, pour t'aider à faire le bon choix pour ton orientation.",
  basePath: '/ecoles/',
  items: [...homePopular.value.popularSchools],
}))

const diplomesPopulaires = computed(() => ({
  title: 'Les diplômes populaires',
  subtitle: 'Tu ne sais pas encore quel diplôme choisir après le bac ?',
  desc: 'Sur PrePeers, découvre les parcours les plus demandés par les étudiants : BTS, BBA, Master, titres certifiés… Trouve la formation qui colle vraiment à ton profil et à tes ambitions de carrière.',
  basePath: '/diplomes/',
  items: [...homePopular.value.popularDiplomas],
}))

const typesEcolesPopulaires = computed(() => ({
  title: "Les types d'écoles populaires",
  subtitle: "Tu te demandes quel type d'école faire après le bac ?",
  desc: 'Découvre les parcours les plus demandés par les étudiants sur PrePeers. Explore les voies académiques les plus adaptés à ton profil.',
  basePath: '/types-ecoles/',
  items: [...homePopular.value.popularSchoolTypes],
}))

const villesPopulaires = computed(() => ({
  title: 'Les villes étudiantes populaires',
  subtitle: 'Tu te demandes dans quelle ville étudier après le bac ?',
  desc: "Découvre les villes les plus choisies par les étudiants sur PrePeers : ambiance, qualité de vie, opportunités… Trouve celle qui te correspond vraiment pour vivre tes meilleures années d'études.",
  basePath: '/ou-etudier/',
  items: [...homePopular.value.popularCities],
}))

const conseilsPopulaires = computed(() => ({
  title: 'Top 10 des conseils les plus lus',
  subtitle: "Tu te demandes quoi faire après le bac ? Tu n'es pas seul(e).",
  desc: "Découvre les conseils les plus lus sur PrePeers : des contenus concrets, pensés pour t'aider à mieux te connaître, faire les bons choix, et avancer sereinement dans ton orientation.",
  basePath: '/nos-conseils/',
  items: [...homePopular.value.popularAdvice],
}))
</script>

<style scoped>
/* ── Hero ─────────────────────────────────────────────────── */
.hero {
  position: relative;
  min-height: 90vh;
  display: flex;
  align-items: center;
  overflow: hidden;
}

.hero__bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.hero__inner {
  position: relative;
  z-index: 1;
  padding-block: var(--space-32) var(--space-24);
}

.hero__label {
  color: var(--accent-blue-lt);
  margin-bottom: var(--space-4);
}

.hero__title {
  font-size: var(--text-hero);
  font-weight: 800;
  letter-spacing: var(--tracking-tight);
  line-height: var(--leading-tight);
  margin-bottom: var(--space-6);
  max-width: 800px;
}

.hero__subtitle {
  font-size: var(--text-lg);
  color: var(--text-secondary);
  line-height: var(--leading-relaxed);
  max-width: 600px;
  margin-bottom: var(--space-10);
}

.hero__actions {
  display: flex;
  gap: var(--space-4);
  flex-wrap: wrap;
  margin-bottom: var(--space-8);
}

.hero__trust {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  flex-wrap: wrap;
}

.hero__trust-item {
  font-size: var(--text-sm);
  color: var(--text-muted);
}

.hero__trust-sep {
  color: var(--border-default);
}

/* ── Section commons ──────────────────────────────────────── */
.section {
  padding-block: var(--space-24);
}

.section--alt {
  background: var(--bg-surface);
}

.section-header {
  margin-bottom: var(--space-12);
}

.section-header__title {
  font-size: var(--text-4xl);
  font-weight: 700;
  margin-top: var(--space-3);
  margin-bottom: var(--space-4);
}

.section-header__desc {
  font-size: var(--text-lg);
  color: var(--text-secondary);
  line-height: var(--leading-relaxed);
  max-width: 560px;
  margin-inline: auto;
}

/* ── Steps ────────────────────────────────────────────────── */
.steps {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-8);
  position: relative;
}

.steps::before {
  content: '';
  position: absolute;
  top: 28px;
  left: calc(33% + var(--space-4));
  right: calc(33% + var(--space-4));
  height: 1px;
  background: var(--border-subtle);
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--space-4);
}

.step__number {
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: var(--tracking-wider);
  color: var(--accent-blue-lt);
}

.step__icon {
  font-size: var(--text-3xl);
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(3, 160, 241, 0.08);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
}

.step__title {
  font-size: var(--text-xl);
  font-weight: 700;
}

.step__desc {
  font-size: var(--text-sm);
  color: var(--text-secondary);
  line-height: var(--leading-relaxed);
}

/* ── Explorer grid ────────────────────────────────────────── */
.explore-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-4);
}

.explore-card {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-5) var(--space-6);
  background: var(--bg-elevated);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  text-decoration: none;
  transition:
    border-color var(--duration-fast) var(--ease-out),
    transform var(--duration-fast) var(--ease-out),
    box-shadow var(--duration-fast) var(--ease-out);
}

.explore-card:hover {
  border-color: var(--border-default);
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.explore-card__icon {
  font-size: var(--text-2xl);
  flex-shrink: 0;
}

.explore-card__body {
  flex: 1;
}

.explore-card__title {
  font-size: var(--text-base);
  font-weight: 700;
  color: var(--text-primary);
}

.explore-card__count {
  font-size: var(--text-xs);
  color: var(--text-muted);
  margin-top: 2px;
}

.explore-card__arrow {
  color: var(--text-muted);
  flex-shrink: 0;
}

/* ── Testimonials ─────────────────────────────────────────── */
.testimonials {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-6);
}

.testimonial {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  padding: var(--space-6);
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
}

.testimonial__quote {
  font-size: var(--text-sm);
  color: var(--text-secondary);
  line-height: var(--leading-relaxed);
  font-style: italic;
  flex: 1;
}

.testimonial__author {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.testimonial__avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--gradient-blue);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--text-xs);
  font-weight: 700;
  color: white;
  flex-shrink: 0;
}

.testimonial__name {
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--text-primary);
}

.testimonial__role {
  font-size: var(--text-xs);
  color: var(--text-muted);
  margin-top: 2px;
}

/* ── CTA final ────────────────────────────────────────────── */
.home-cta {
  background: var(--gradient-hero);
  border-top: 1px solid var(--border-subtle);
}

.home-cta__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--space-6);
}

.home-cta__title {
  font-size: var(--text-4xl);
  font-weight: 800;
  letter-spacing: var(--tracking-tight);
}

.home-cta__desc {
  font-size: var(--text-lg);
  color: var(--text-secondary);
}

/* ── Responsive ───────────────────────────────────────────── */
@media (max-width: 1024px) {
  .explore-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .testimonials {
    grid-template-columns: 1fr;
  }
  .steps {
    grid-template-columns: 1fr;
  }
  .steps::before {
    display: none;
  }
}

@media (max-width: 640px) {
  .hero__actions {
    flex-direction: column;
  }
  .explore-grid {
    grid-template-columns: 1fr;
  }
}
</style>
